import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { KpiSmallRatioData, KpiSmallVariant, WhatsNewKpiConfig } from '@uigy/modules/overview/kpis/kpi.interfaces';
import { TimeFrameItem } from '@uigy/shared/services/time-frame.service';
import {
  KpiSmallRatioComponent,
} from '@uigy/shared/components/widgets/kpi-small/kpi-small-ratio/kpi-small-ratio.component';
import { KpiSmallComponent } from '@uigy/shared/components/widgets/kpi-small/kpi-small.component';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
    selector: 'app-whats-new-kpi-widget',
    imports: [
        KpiSmallComponent,
        AsyncPipe,
        NgTemplateOutlet,
        KpiSmallRatioComponent,
    ],
    templateUrl: './whats-new-kpi-widget.component.html',
    styleUrl: './whats-new-kpi-widget.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WhatsNewKpiWidgetComponent {
  public config = input.required<WhatsNewKpiConfig>();
  public isLoading = signal<boolean>(false);
  public count$?: Observable<number>;
  public data$?: Observable<KpiSmallRatioData>;

  public basicTemplate = viewChild('basicTemplate', {read: TemplateRef});
  public ratioTemplate = viewChild('ratioTemplate', {read: TemplateRef});

  public selectedTemplate = computed(() => {
    const config = this.config();
    const variant = config?.kpiVariant ?? KpiSmallVariant.Basic;
    return variant === KpiSmallVariant.Basic
      ? this.basicTemplate()
      : this.ratioTemplate();
  });

  private cdr = inject(ChangeDetectorRef);

  constructor() {
    effect(() => {
      const config = this.config();
      if (config) {
        this.fetchKpiData();
      }
    });
  }

  private fetchKpiData(): void {
    const config = this.config();
    if (!config) {
      return;
    }

    this.setLoadingState(true);

    if (config.fetchData) {
      const filter = config.filter?.(config.timeFrameValue as TimeFrameItem) ?? undefined;

      if (config.kpiVariant === KpiSmallVariant.Ratio) {
        this.data$ = (config.fetchData(filter) as Observable<KpiSmallRatioData>).pipe(
          tap(() => this.setLoadingState(false))
        );
      } else {
        this.count$ = (config.fetchData(filter) as Observable<number>).pipe(
          tap(() => this.setLoadingState(false))
        );
      }
    } else {
      this.setLoadingState(false);
    }
  }

  private setLoadingState(state: boolean): void {
    this.isLoading.set(state);
    this.cdr.markForCheck();
  }
}
